/*
  The wishlist, everywhere.

  Loaded on every page from layout/theme.liquid, because the heart on a product card is
  on every page that lists products. It owns one thing: the ordered set of favourited
  product ids, and keeping every heart and the header badge in step with it.

  WHERE THE SET LIVES

  localStorage is the working copy and it is always written. The durable copy belongs to
  the Wishlist Hero app: this file talks to its JS SDK (window.WishListHero_SDK), which
  is what makes a list survive a new device, and which owns the account side of it
  entirely — the theme no longer reads or writes customer.metafields.custom.favorites,
  and the theme's own favourites app is retired.

  Two consequences worth knowing about, both deliberate:

  - The SDK only exists once the app's script has booted, so nothing here can paint from
    it on the first frame. localStorage is what the hearts are drawn from, and the SDK
    reconciles a moment later. A shopper on a device they have used before sees the
    right hearts immediately; one on a brand new device sees hollow for a beat.

  - Hero keys its wishlist by variant, not by product. The theme keys by product, which
    is what the wishlist page's card registry is built around, so every button carries
    its variant id (see snippets/favorite-button.liquid) and this file translates.

  A failed SDK call is allowed to fail: localStorage already holds the same set, so the
  page stays correct and the next toggle tries again.

  ORDER

  Newest first, which is the order the wishlist page renders. Hero stamps each item with
  AddedOn, so a set that comes back from the server sorts into the same order.
*/

(function () {
  'use strict';

  var STORAGE_KEY = 'dutcher:favorites';

  /* Safari in private mode throws on localStorage rather than returning null, and a
     shopper with storage blocked should still be able to use the page for one session —
     so every access is guarded and falls back to an in-memory copy. */
  var memoryFallback = null;

  function readLocal() {
    if (memoryFallback) return memoryFallback.slice();
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.map(String);
      if (parsed && Array.isArray(parsed.ids)) return parsed.ids.map(String);
      return [];
    } catch (error) {
      return [];
    }
  }

  function writeLocal(ids) {
    memoryFallback = ids.slice();
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, ids: ids }));
      memoryFallback = null;
    } catch (error) {
      /* Keep the in-memory copy: it is all this session has. */
    }
  }

  /* Union that keeps the first occurrence of each id. Local-only ids come first because
     anything the shopper hearted in this browser is newer than anything already on the
     server — there are no timestamps to compare across the two, and recency is the only
     ordering the page claims.

     Union also means a removal that never reached the server — the shopper un-hearted
     something while offline and then closed the tab — comes back on the next visit. The
     alternative is letting the server delete things the shopper still has locally, which
     is the worse way to be wrong about a wishlist. */
  function union(first, second) {
    var seen = Object.create(null);
    var out = [];
    [first, second].forEach(function (list) {
      list.forEach(function (id) {
        var key = String(id);
        if (!key || seen[key]) return;
        seen[key] = true;
        out.push(key);
      });
    });
    return out;
  }

  var state = readLocal();
  writeLocal(state);

  /* --- Wishlist Hero ---

     Everything the app needs to store an item, keyed by our product id. Filled from the
     button the shopper clicked, and from whatever the server hands back on hydration —
     the second source is what lets the page delete an item whose card is not on it. */
  var payloads = Object.create(null);

  var sdk = null;
  var sdkQueue = [];
  var hydrated = false;
  var session = null;

  function remember(id, payload) {
    if (!id || !payload || !payload.ProductVariantId) return;
    payloads[String(id)] = payload;
  }

  function payloadFromButton(button) {
    var variant = button.getAttribute('data-favorite-variant');
    if (!variant) return null;
    return {
      ProductId: Number(button.getAttribute('data-favorite-id')),
      ProductVariantId: Number(variant),
      ProductTitle: button.getAttribute('data-favorite-title') || '',
      ProductPrice: Number(button.getAttribute('data-favorite-price')) || 0,
      ProductLink: button.getAttribute('data-favorite-link') || '',
      ProductImage: button.getAttribute('data-favorite-image') || ''
    };
  }

  /* The two things every write needs, resolved once and reused.

     GetWishListHash(true) creates a wishlist if the shopper has none yet, which is what
     the very first add needs. The app documents it as usable either synchronously or
     with await.

     GetCustomerId is named in the app's method list but has no documented signature, so
     it is treated as possibly returning a value, possibly a promise, and possibly
     throwing. All three land on the same place. An absent customer id is not fatal — the
     item is then stored against the wishlist hash alone, which is how a guest's list
     works anyway — but it IS the difference between a list that follows the account and
     one that follows the browser, so it is worth checking against the real SDK. */
  function openSession() {
    if (session) return session;

    session = Promise.all([
      Promise.resolve(sdk.GetWishListHash(true)),
      new Promise(function (resolve) {
        resolve(sdk.GetCustomerId());
      }).catch(function () {
        return null;
      })
    ]).then(function (values) {
      return { hash: values[0], customer: Number(values[1]) || null };
    });

    return session;
  }

  /* Runs `job(sdk, hash, customerId)` once the app is up and the session resolved. */
  function withSdk(job) {
    if (!sdk) {
      sdkQueue.push(job);
      return;
    }
    openSession()
      .then(function (open) {
        /* No hash, no writes. localStorage still holds the set. */
        if (open.hash) job(sdk, open.hash, open.customer);
      })
      .catch(function () {});
  }

  function remoteAdd(id) {
    var payload = payloads[String(id)];
    if (!payload) return;
    withSdk(function (api, hash, customer) {
      var item = {
        ProductId: payload.ProductId,
        ProductVariantId: payload.ProductVariantId,
        ProductTitle: payload.ProductTitle,
        ProductPrice: payload.ProductPrice,
        ProductLink: payload.ProductLink,
        ProductImage: payload.ProductImage,
        WishlistHash: hash
      };
      if (customer) item.CustomerId = customer;
      Promise.resolve(api.AddWishListItem(item)).catch(function () {});
    });
  }

  function remoteRemove(id) {
    var payload = payloads[String(id)];
    if (!payload) return;
    withSdk(function (api, hash) {
      Promise.resolve(
        api.DeleteWishListItem({
          ProductVariantId: payload.ProductVariantId,
          WishlistHash: hash
        })
      ).catch(function () {});
    });
  }

  /* What the app already holds, newest first. Its items carry the variant id, so this
     doubles as the payload source for products whose card is not on this page. */
  function serverState() {
    var items;
    try {
      items = sdk.GetWishListItems() || [];
    } catch (error) {
      return [];
    }

    return items
      .slice()
      .sort(function (a, b) {
        return new Date(b.AddedOn || 0) - new Date(a.AddedOn || 0);
      })
      .map(function (item) {
        var id = String(item.ProductId);
        remember(id, {
          ProductId: Number(item.ProductId),
          ProductVariantId: Number(item.ProductVariantId),
          ProductTitle: item.ProductName || '',
          ProductPrice: Number(item.Price) || 0,
          ProductLink: item.ProductLink || '',
          ProductImage: item.ProductImage || ''
        });
        return id;
      })
      .filter(Boolean);
  }

  function hydrate() {
    if (hydrated) return;
    hydrated = true;

    var server = serverState();
    var merged = union(state, server);

    /* Anything this browser had that the app does not: hearted before the SDK woke up,
       or on a visit where the write never landed. Push it now. */
    state.forEach(function (id) {
      if (server.indexOf(id) === -1) remoteAdd(id);
    });

    if (merged.length !== state.length) {
      state = merged;
      writeLocal(state);
      paintAll();
      announce();
    }
  }

  function onSdkReady() {
    if (sdk) return;
    sdk = window.WishListHero_SDK;
    if (!sdk) return;

    if (sdkWatch) {
      window.clearInterval(sdkWatch);
      sdkWatch = null;
    }

    var queued = sdkQueue;
    sdkQueue = [];
    queued.forEach(function (job) {
      withSdk(job);
    });

    hydrate();
  }

  /* The event above is the app's documented signal and should be the one that fires.
     This is the backstop for the case it does not: the theme has no way to be told the
     app renamed it, and the failure is silent — the wishlist keeps working out of
     localStorage and simply never reaches the account. Ten seconds of polling costs
     nothing and turns that into a non-event. */
  var sdkWatch = null;

  function watchForSdk() {
    var tries = 0;
    sdkWatch = window.setInterval(function () {
      tries += 1;
      if (window.WishListHero_SDK) {
        onSdkReady();
        return;
      }
      if (tries >= 40) {
        window.clearInterval(sdkWatch);
        sdkWatch = null;
      }
    }, 250);
  }

  /* --- DOM --- */

  function paintButton(button) {
    var id = button.getAttribute('data-favorite-id');
    if (!id) return;
    var active = state.indexOf(String(id)) !== -1;

    button.setAttribute('aria-pressed', active ? 'true' : 'false');

    var label = button.getAttribute(active ? 'data-favorite-label-on' : 'data-favorite-label-off');
    if (label) button.setAttribute('aria-label', label);

    var icon = button.closest('.icon');
    if (icon) icon.classList.toggle('active', active);
  }

  function paintCount() {
    var holders = document.querySelectorAll('[data-favorites-count]');
    Array.prototype.forEach.call(holders, function (holder) {
      var badge = holder.querySelector('.icon-number');
      if (state.length === 0) {
        holder.classList.remove('has-number');
        if (badge) badge.remove();
        return;
      }
      holder.classList.add('has-number');
      if (!badge) {
        badge = document.createElement('div');
        badge.className = 'icon-number';
        holder.appendChild(badge);
      }
      badge.textContent = String(state.length);
    });
  }

  function paintAll(root) {
    var scope = root || document;
    var buttons = scope.querySelectorAll('[data-favorite-toggle]');
    Array.prototype.forEach.call(buttons, paintButton);
    paintCount();
  }

  function announce() {
    document.dispatchEvent(
      new CustomEvent('dutcher:favorites:change', { detail: { ids: state.slice() } })
    );
  }

  function commit(next) {
    state = next;
    writeLocal(state);
    paintAll();
    announce();
  }

  function toggle(id) {
    var key = String(id);
    if (!key) return false;
    var index = state.indexOf(key);
    if (index === -1) {
      commit([key].concat(state));
      remoteAdd(key);
      return true;
    }
    var next = state.slice();
    next.splice(index, 1);
    commit(next);
    remoteRemove(key);
    return false;
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    var button = target && target.closest ? target.closest('[data-favorite-toggle]') : null;
    if (!button) return;
    event.preventDefault();
    /* A primary card wraps itself in a full-bleed link to the product; without this the
       toggle also navigates away from the page it just changed. */
    event.stopPropagation();

    var id = button.getAttribute('data-favorite-id');
    remember(id, payloadFromButton(button));
    toggle(id);
  });

  window.DutcherFavorites = {
    list: function () {
      return state.slice();
    },
    has: function (id) {
      return state.indexOf(String(id)) !== -1;
    },
    toggle: toggle,
    add: function (id) {
      if (state.indexOf(String(id)) !== -1) return;
      commit([String(id)].concat(state));
      remoteAdd(id);
    },
    remove: function (id) {
      var index = state.indexOf(String(id));
      if (index === -1) return;
      var next = state.slice();
      next.splice(index, 1);
      commit(next);
      remoteRemove(id);
    },
    /* The wishlist page clones cards in after this file has already painted, so it needs
       a way to paint the hearts it just added. Cloned cards also carry payloads the
       store has not seen yet, which is what lets the page remove them again. */
    refresh: function (root) {
      var scope = root || document;
      Array.prototype.forEach.call(
        scope.querySelectorAll('[data-favorite-toggle]'),
        function (button) {
          remember(button.getAttribute('data-favorite-id'), payloadFromButton(button));
        }
      );
      paintAll(root);
    },
    syncing: function () {
      return sdk !== null;
    }
  };

  /* Payloads for everything already on the page, so a removal works before the shopper
     has clicked anything — and so the hydration below has something to push with when
     the app turns out to already be up. */
  window.DutcherFavorites.refresh();
  announce();

  document.addEventListener('wishlist-hero-wishlist-sdk-ready', onSdkReady);
  /* The app embed may well have booted before this deferred file ran, and the ready
     event is gone by then — the object itself is the other half of the check. */
  if (window.WishListHero_SDK) {
    onSdkReady();
  } else {
    watchForSdk();
  }
})();
